import { useState, useEffect } from "react";
import { Users, UserPlus, Mail, Shield, ShieldCheck, Trash2, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";

export default function UserManagement() {
  const { getAuthHeaders, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "sales" });
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        toast.error("Kunde inte ladda användare");
      }
    } catch (error) {
      console.error("Failed to load users:", error);
      toast.error("Kunde inte ladda användare");
    } finally {
      setLoading(false);
    }
  };

  const inviteUser = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) return;

    setInviting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(inviteForm)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || "Inbjudan skickad!");
        setShowInviteModal(false);
        setInviteForm({ email: "", name: "", role: "sales" });
        loadUsers();
      } else {
        toast.error(data.detail || "Kunde inte bjuda in användare");
      }
    } catch (error) {
      toast.error("Något gick fel");
    } finally {
      setInviting(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/role`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId, role: newRole })
      });

      if (response.ok) {
        toast.success("Roll uppdaterad");
        loadUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Kunde inte uppdatera roll");
      }
    } catch (error) {
      toast.error("Något gick fel");
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/status?status=${newStatus}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(newStatus === "active" ? "Användare aktiverad" : "Användare inaktiverad");
        loadUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Kunde inte uppdatera status");
      }
    } catch (error) {
      toast.error("Något gick fel");
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Är du säker på att du vill radera ${email}?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Användare raderad");
        loadUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Kunde inte radera användare");
      }
    } catch (error) {
      toast.error("Något gick fel");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Aldrig";
    return new Date(dateString).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "Aldrig";
    return new Date(dateString).toLocaleDateString("sv-SE", {
      month: "short",
      day: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="w-6 sm:w-7 h-6 sm:h-7 text-[#255E69]" />
            Användare
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Hantera användare och roller
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#255E69] text-white rounded-xl hover:bg-[#1e4a53] transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          <span className="sm:inline">Bjud in</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
          <div className="text-xl sm:text-2xl font-bold text-gray-900">{users.length}</div>
          <div className="text-xs sm:text-sm text-gray-500">Totalt</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
          <div className="text-xl sm:text-2xl font-bold text-gray-900">
            {users.filter(u => u.role === "admin").length}
          </div>
          <div className="text-xs sm:text-sm text-gray-500">Admins</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
          <div className="text-xl sm:text-2xl font-bold text-gray-900">
            {users.filter(u => u.role === "sales").length}
          </div>
          <div className="text-xs sm:text-sm text-gray-500">Säljare</div>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden space-y-3">
        {users.map((user) => (
          <div key={user.user_id} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#255E69]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#255E69] font-medium">
                    {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900">{user.name || "Ej angivet"}</p>
                    {user.user_id === currentUser?.user_id && (
                      <span className="px-1.5 py-0.5 text-[10px] bg-[#255E69]/10 text-[#255E69] rounded-full">
                        Du
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate max-w-[180px]">{user.email}</p>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                user.status === "active"
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-600"
              }`}>
                {user.status === "active" ? "Aktiv" : "Inaktiv"}
              </span>
            </div>
            
            <div className="mt-3 flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-gray-400" />
                <select
                  value={user.role}
                  onChange={(e) => updateUserRole(user.user_id, e.target.value)}
                  disabled={user.user_id === currentUser?.user_id}
                  className="px-2 py-1 border border-gray-200 rounded-lg text-sm bg-white disabled:opacity-50"
                >
                  <option value="admin">Admin</option>
                  <option value="sales">Säljare</option>
                </select>
              </div>
              <span className="text-xs text-gray-400">
                {formatDateShort(user.last_login)}
              </span>
            </div>
            
            {user.user_id !== currentUser?.user_id && (
              <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
                <button
                  onClick={() => toggleUserStatus(user.user_id, user.status)}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg ${
                    user.status === "active" 
                      ? "text-gray-600 bg-gray-100" 
                      : "text-green-600 bg-green-50"
                  }`}
                >
                  {user.status === "active" ? (
                    <><X className="w-4 h-4" /> Inaktivera</>
                  ) : (
                    <><Check className="w-4 h-4" /> Aktivera</>
                  )}
                </button>
                <button
                  onClick={() => deleteUser(user.user_id, user.email)}
                  className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Användare</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Roll</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Senaste inloggning</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Åtgärder</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((user) => (
                <tr key={user.user_id} className="hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#255E69]/10 rounded-full flex items-center justify-center">
                        <span className="text-[#255E69] font-medium">
                          {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                      {user.user_id === currentUser?.user_id && (
                        <span className="px-2 py-0.5 text-xs bg-[#255E69]/10 text-[#255E69] rounded-full">
                          Du
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <select
                      value={user.role}
                      onChange={(e) => updateUserRole(user.user_id, e.target.value)}
                      disabled={user.user_id === currentUser?.user_id}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#255E69]/20 disabled:opacity-50"
                    >
                      <option value="admin">Admin</option>
                      <option value="sales">Säljare</option>
                    </select>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                      user.status === "active"
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-600"
                    }`}>
                      {user.status === "active" ? (
                        <><Check className="w-3 h-3" /> Aktiv</>
                      ) : (
                        <><X className="w-3 h-3" /> Inaktiv</>
                      )}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-500">
                    {formatDate(user.last_login)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      {user.user_id !== currentUser?.user_id && (
                        <>
                          <button
                            onClick={() => toggleUserStatus(user.user_id, user.status)}
                            className="p-2 text-gray-400 hover:text-[#255E69] transition-colors"
                            title={user.status === "active" ? "Inaktivera" : "Aktivera"}
                          >
                            {user.status === "active" ? (
                              <X className="w-4 h-4" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => deleteUser(user.user_id, user.email)}
                            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                            title="Radera"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Bjud in användare</h2>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={inviteUser} className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-postadress *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="namn@foretag.se"
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#255E69]/20"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Namn
                </label>
                <input
                  type="text"
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Förnamn Efternamn"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#255E69]/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Roll
                </label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#255E69]/20 bg-white"
                >
                  <option value="sales">Säljare</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="pt-4 flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  disabled={inviting || !inviteForm.email}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#255E69] text-white rounded-lg font-medium hover:bg-[#1e4a53] transition-colors disabled:opacity-50"
                >
                  {inviting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <UserPlus className="w-5 h-5" />
                      Skicka inbjudan
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-3 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
